import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { redirectToSignIn } from '../../utils';
import { FEATURE_WIZARDS_FLAG, handlesSignedOutItself } from './constants';
import { WizardLanding } from './WizardLanding';

/**
 * The two gates a landing has to clear, in the order that IS the rule.
 *
 * The flag is consulted before anything else, including the sign-in bounce: a host that bounced
 * anonymous traffic before reading the flag would leave every circulating link force-redirecting
 * to sign-in after the flag had been switched off to stop an incident — precisely the case the
 * kill switch exists for. With the flag off nothing is looked up, nothing is recorded, no
 * redirect happens and the parameter is left exactly as it arrived.
 */
export function WizardGate({ slug }: { slug: string }) {
  const { pathname } = useLocation();
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { enabled, loading: flagLoading } = useFeatureFlag(FEATURE_WIZARDS_FLAG);
  const bouncedRef = useRef(false);
  const allowed = !flagLoading && enabled;

  /**
   * The signed-out bounce, for the routes that have none of their own. `redirectToSignIn()` with
   * no argument already encodes `pathname + search + hash` into `redirect_url` on town-square
   * /signin, so the wizard parameter survives sign-in with no second return-URL mechanism — and
   * on a protected route this host stays out of the way and lets `ProtectedRoute` do exactly the
   * same thing. Waiting for Clerk to finish loading matters: acting early would send an
   * already-signed-in reader to the sign-in page.
   *
   * Eligibility is the one thing a signed-out visitor cannot evaluate, so the decision rests on
   * the parameter's presence alone and the post-sign-in lookup settles whether anything opens.
   * Nothing is recorded and the parameter is left alone, so a visitor who abandons sign-in still
   * has a working link on a later tap.
   */
  useEffect(() => {
    if (!allowed || !isLoaded || isSignedIn) return;
    if (handlesSignedOutItself(pathname)) return;
    if (bouncedRef.current) return;
    bouncedRef.current = true;
    redirectToSignIn();
  }, [allowed, isLoaded, isSignedIn, pathname]);

  if (!allowed || !isSignedIn) return null;

  return <WizardLanding slug={slug} />;
}
